import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { createGlobalStyle } from 'styled-components';

import { useVideoCreatorStore } from '@src/stores-v2';
import { useBRollUpload } from './BRollUploadContext';
import BRollUploadAsset from './BRollUploadAsset';
import BRollUploadDropzone from './BRollUploadDropzone';
import EmailModal from './EmailModal';
import OrganizationAvatar from '../OrganizationAvatar';
import ToastNotification from '../common/ToastNotification';
import SpinningLoading from '../SpinningLoading';
import Modal from '../common/Modal';

import ArborLogo from '@src/svgs/ArborLogo';

const BRollUploadPage = observer(() => {
  const {
    bRollAssets,
    bRollService,
    hasPendingAssets,
    hasUploadingAssets,
    isLoading,
    organization,
    story,
  } = useBRollUpload();
  const videoCreator = useVideoCreatorStore();

  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const album = organization || story?._allReferencingShowcases?.[0];

  return (
    <Container>
      <GlobalStyle />
      <Header>
        <ArborLogo />

        {album && (
          <HeaderRight>
            <OrganizationAvatar
              album={album}
              username={story?.storyTeller.name}
              logoSize={40}
            />
          </HeaderRight>
        )}
      </Header>

      <Content>
        <Title>Upload photos and videos to make your story come to life</Title>

        {isLoading && !bRollAssets.length ? (
          <SpinningLoading
            Ico={null}
            text="Gathering your assets..."
            customStyle={{
              position: 'relative',
              backgroundColor: 'transparent',
              padding: '64px 0',
            }}
          />
        ) : (
          <AssetGrid>
            <BRollUploadDropzone />
            {bRollAssets.map((asset) => (
              <BRollUploadAsset key={asset.id} asset={asset} />
            ))}
          </AssetGrid>
        )}
      </Content>

      {(hasPendingAssets || hasUploadingAssets) && (
        <Footer>
          <AcknowledgeText>
            By uploading your assets, you agree to the{' '}
            <span onClick={() => setIsTermsOpen(true)}>Terms of Use</span> of
            Arbor.
          </AcknowledgeText>
          <UploadButton
            disabled={hasUploadingAssets}
            onClick={() => bRollService.uploadPendingAssets()}
          >
            {hasUploadingAssets ? 'Uploading...' : 'Upload'}
          </UploadButton>
        </Footer>
      )}

      {isTermsOpen && (
        <Modal
          isOpen={true}
          closeModal={() => setIsTermsOpen(false)}
          backgroundColor="#03041977"
        >
          <TermsContent>
            <TermsHeader>Photo Terms</TermsHeader>
            <TermsBody>
              I represent and warrant that I own the rights in any photographs,
              images, videos, audio recordings, or other content that I provide
              to Arbor, and that I have the right to provide such content to
              Arbor without violating any other person's intellectual property
              rights, privacy rights, or other rights, laws, or regulations.
            </TermsBody>
          </TermsContent>
        </Modal>
      )}

      <EmailModal />

      {videoCreator.toastState && <ToastNotification />}
    </Container>
  );
});

const GlobalStyle = createGlobalStyle`
  body {
    background-color: #030419;
  }
`;

const Container = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100dvh;
  width: 100%;
  gap: 32px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 2000px;
  width: 100%;
  padding: 32px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  text-align: right;
`;

const Content = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1648px;
  width: 100%;
  @media (min-width: 768px) {
    gap: 64px;
  }
`;

const Title = styled.h1`
  align-self: center;
  font-weight: 700;
  font-size: 20px;
  text-align: center;
  color: #f3e9d7;
  padding: 0 24px;
  margin: 0;
  @media (min-width: 540px) {
    font-size: 24px;
  }
  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const AssetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 24px;
  padding: 0 24px;
  @media (min-width: 540px) {
    padding: 0 24px 64px;
    gap: 32px;
  }
`;

const Footer = styled.footer`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  width: 100%;
  position: sticky;
  bottom: 0;
  background-color: #030419;
  border-top: 1px solid #484848;
`;

const AcknowledgeText = styled.p`
  font-size: 12px;
  color: #f3e9d7;
  text-align: center;
  margin: 0;
  line-height: 1.4;
  max-width: 400px;
  width: 100%;
  margin-bottom: 24px;
  span {
    color: #45d483;
    cursor: pointer;
  }
`;

const UploadButton = styled.button`
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background-color: #17c964;
  color: #03041a;
  border: none;
  transition: 0.2s ease;
  &:hover {
    background-color: #14b357;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  @media (min-width: 460px) {
    max-width: 240px;
  }
`;

const TermsContent = styled.div`
  width: 100%;
  background-color: #030419;
  box-shadow: 8px 8px 16px 0px rgba(0, 0, 0, 0.4);
  border: 1px solid #484848;
  border-radius: 10px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  &,
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  @media (min-width: 460px) {
    max-width: 460px;
  }
`;

const TermsHeader = styled.h3`
  color: #45d483;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1.92px;
  margin: 0;
`;

const TermsBody = styled.p`
  color: #f3e9d7;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.6;
  text-align: center;
  margin: 0;
`;

export default BRollUploadPage;
